<script lang='ts'>
import type {
  Ref,
} from 'vue'
import { CircleCheckFilled } from '@element-plus/icons-vue'
import {
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'

export interface IStepsProps {
  space: number | string
  active: number
  direction: string
  alignCenter: boolean
  simple: boolean
  finishStatus: string
  processStatus: string
}

export interface StepItemState {
  uid: number
  currentStatus: string
  setIndex: (val: number) => void
  calcProgress: (status: string) => void
}

export interface IStepsInject {
  props: IStepsProps
  steps: Ref<StepItemState[]>
}

export default defineComponent({
  name: 'MsStep',
  components: {
    CircleCheckFilled,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
      validator: (val: string): boolean =>
        ['', 'wait', 'process', 'finish', 'error', 'success'].includes(val),
    },
  },
  setup(props) {
    const index = ref(-1)
    const lineStyle = ref({})
    const internalStatus = ref('')
    const parent: IStepsInject = inject('MsSteps')!
    const currentInstance = getCurrentInstance()

    const currentStatus = computed(() => {
      return props.status || internalStatus.value
    })
    const prevStatus = computed(() => {
      const prevStep = parent.steps.value[index.value - 1]
      return prevStep ? prevStep.currentStatus : 'wait'
    })
    const isCenter = computed(() => {
      return parent.props.alignCenter
    })
    const isVertical = computed(() => {
      return parent.props.direction === 'vertical'
    })
    const stepsCount = computed(() => {
      return parent.steps.value.length
    })
    const isLast = computed(() => {
      return (
        parent.steps.value[stepsCount.value - 1]?.uid === currentInstance?.uid
      )
    })
    const setIndex = (val) => {
      index.value = val
    }
    const updateStatus = (activeIndex) => {
      if (activeIndex > index.value) {
        internalStatus.value = parent.props.finishStatus
      } else if (activeIndex === index.value && prevStatus.value !== 'error') {
        internalStatus.value = parent.props.processStatus
      } else {
        internalStatus.value = 'wait'
      }
      const prevChild = parent.steps.value[stepsCount.value - 1]
      if (prevChild) prevChild.calcProgress(internalStatus.value)
    }
    const calcProgress = (status) => {
      let step = 100
      const style: Record<string, unknown> = {}
      style.transitionDelay = `${150 * index.value}ms`
      if (status === parent.props.processStatus) {
        step = 0
      } else if (status === 'wait') {
        step = 0
        style.transitionDelay = `${-150 * index.value}ms`
      }
      style[parent.props.direction === 'vertical' ? 'height' : 'width'] = `${step}%`
      lineStyle.value = style
    }
    const stepItemState = reactive({
      uid: computed(() => currentInstance!.uid),
      currentStatus,
      setIndex,
      calcProgress,
    })
    parent.steps.value = [...parent.steps.value, stepItemState]
    onMounted(() => {
      watch(
        [
          () => parent.props.active,
          () => parent.props.processStatus,
          () => parent.props.finishStatus,
        ],
        ([active]) => {
          updateStatus(active)
        },
        { immediate: true },
      )
    })
    onBeforeUnmount(() => {
      parent.steps.value = parent.steps.value.filter(
        instance => instance.uid !== currentInstance?.uid,
      )
    })
    return {
      index,
      lineStyle,
      currentStatus,
      isCenter,
      isVertical,
      isLast,
      parent,
      setIndex,
      updateStatus,
    }
  },
})
</script>

<template>
  <div class="step-container" :class="[isVertical ? `is-vertical` : 'is-horizontal']">
    <div class="flex header-wrapper items-center" :class="[`is-${currentStatus}`]">
      <div class="header text-bold">
        <ms-svg-icon v-if="!isVertical && currentStatus === 'finish'" icon="ic_finish" size="16" color="#A1A1A1" />
        <template v-else>
          {{ `${index + 1}` }}
        </template>
      </div>
      <div class="title-wrapper">
        <slot v-if="$slots.title" name="title" />
        <div v-else class="text-bold text-base">
          {{ title }}
        </div>
      </div>
      <el-icon v-if="isVertical && currentStatus === 'finish'" class="text-success">
        <CircleCheckFilled />
      </el-icon>
      <ms-svg-icon v-if="!isVertical && !isLast" class="m-mx-md" icon="ic_arrow_right" size="16" />
    </div>
    <div v-if="isVertical" class="flex items-stretch">
      <div v-if="!isLast" class="left m-mr-md">
        <div class="divider" />
      </div>
      <div v-show="currentStatus === 'process'" class="right" :class="[`${isLast ? 'is-last' : ''}`]">
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<style lang='scss' scoped>
.step-container {
  &.is-vertical {
    .header {
      margin-right: $space-md;
    }
  }

  &.is-horizontal {
    .header {
      margin-right: $space-sm;
    }

    .title-wrapper {
      color: $font-color-secondary;
    }

    .is-process {
      .title-wrapper {
        color: $font-color-common;
      }
    }
  }

  .header-wrapper {
    .header {
      font-size: 10px;
      width: 24px;
      height: 20px;
      background-color: colorWithAlpha($alpha: 0.1);
      color: $font-color-secondary;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: $border-radius-lg;
    }

    &.is-process {
      .header {
        background-color: $color-theme;
        color: white;
      }
    }

    .title-wrapper {
      flex: 1;
    }
  }

  .left {
    width: 24px;
    padding: $space-xs 0;

    .divider {
      margin: 0 auto;
      width: 4px;
      min-height: 16px;
      height: 100%;
      border-radius: 2px;
      background-color: colorWithAlpha($alpha: 0.1);
    }
  }

  .right {
    flex: 1;

    &.is-last {
      margin-left: 40px;
    }
  }
}
</style>
